import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from "react";
import { Box, Collapse, Fade, Stack, Tab, Tabs, Typography, } from "@mui/material";
function resolveThemeOverride(slot, theme, ownerState) {
    if (!slot) {
        return {};
    }
    return typeof slot === "function" ? slot({ theme, ownerState }) : slot;
}
function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}
function CustomTabPanel(props) {
    const { children, value, index, dir, tab, ...other } = props;
    return (_jsx(Box, { role: "tabpanel", hidden: value !== index, id: `simple-tabpanel-${index}`, "aria-labelledby": `simple-tab-${index}`, dir: dir, ...other, sx: { width: "100%" }, children: value === index && (_jsx(Box, { sx: {
                backgroundImage: {
                    xs: "none",
                    md: tab.removeBgImage ? "none" : "url('/interface-bg.svg')",
                },
                backgroundSize: "cover",
                backgroundColor: tab?.removeBgImage ? tab?.sectionBgColor : "",
                p: {
                    xs: tab?.removeBgImage ? 2.5 : 0,
                    md: tab?.removeBgImage ? 5 : 3,
                },
                border: {
                    xs: tab.removeBgImage ? "none" : "4px solid",
                    md: "none",
                },
                borderColor: { xs: "primary.main" },
                "& > .MuiBox-root:first-child": {
                    transform: "translateY(4px)",
                    pt: { xs: undefined, md: dir === "vertical" ? 0 : undefined },
                    pb: { xs: undefined, md: dir === "vertical" ? 0 : undefined },
                },
                "& .MuiContainer-root": {
                    px: "24px !important",
                },
                "& ul li::before": {
                    backgroundColor: `${tab?.borderColor || "#0000FF"} !important`,
                },
            }, children: children })) }));
}
const DescriptionTabsComponent = (props) => {
    const { direction = "horizontal", style = "text", color = "primary", dynamicList = [], } = props;
    const [value, setValue] = useState(0);
    const handleChange = (_event, newValue) => {
        setValue(newValue);
    };
    const baseTabSx = useMemo(() => {
        if (direction === "vertical") {
            return {
                opacity: 1,
                alignItems: "flex-start",
                flexShrink: 0,
                minWidth: "max-content",
                textAlign: "left",
                whiteSpace: "nowrap",
            };
        }
        return {
            opacity: 1,
            flexShrink: 0,
            minWidth: "max-content",
            whiteSpace: "nowrap",
        };
    }, [direction]);
    return (_jsxs(Stack, { spacing: 4, direction: {
            xs: "column",
            md: direction === "vertical" ? "row" : "column",
        }, children: [_jsx(Stack, { sx: { ...(direction === "vertical" && { width: { md: "40%" } }) }, children: _jsx(Tabs, { value: value, onChange: handleChange, variant: "standard", orientation: direction, sx: {
                        "& .MuiTabs-list": {
                            gap: { xs: 1, md: 1.5 },
                        },
                        "& .MuiTabs-indicator": {
                            display: "none",
                        },
                        ...(style === "contained"
                            ? {
                                "& .MuiTabs-indicator": {
                                    display: "none",
                                },
                            }
                            : {}),
                    }, children: dynamicList?.map((tab, index) => (_jsx(Tab, { label: _jsxs(Stack, { sx: { gap: value === index && tab?.description ? 1.5 : 0 }, children: [_jsx(Typography, { color: style === "contained" ? "inherit" : "text.primary", sx: {
                                        fontWeight: 600,
                                        lineHeight: "125%",
                                        fontSize: 16,
                                        letterSpacing: "2%",
                                    }, children: tab.title }), tab?.description && (_jsx(Collapse, { in: value === index, timeout: 300, children: _jsx(Typography, { color: style === "contained" ? "inherit" : "text.secondary", sx: { textWrap: "auto" }, children: tab.description }) }, tab.id))] }), ...a11yProps(index), sx: style === "contained"
                            ? (theme) => ({
                                ...baseTabSx,
                                ...resolveThemeOverride(theme.components?.MuiButton?.styleOverrides?.root, theme, {
                                    variant: "contained",
                                    color,
                                }),
                                // Désactive l'animation d'underline au hover héritée du MuiButton
                                "&::after": { display: "none" },
                                "&:not(.Mui-disabled):hover::after": {
                                    transform: "none",
                                },
                                "&.Mui-selected": {
                                    ...resolveThemeOverride(theme.components?.MuiButton?.styleOverrides
                                        ?.contained, theme, {
                                        variant: "contained",
                                        color,
                                    }),
                                    ...resolveThemeOverride(color === "primary"
                                        ? theme.components?.MuiButton?.styleOverrides
                                            ?.containedPrimary
                                        : theme.components?.MuiButton?.styleOverrides
                                            ?.containedSecondary, theme, {
                                        variant: "contained",
                                        color,
                                    }),
                                },
                            })
                            : (theme) => {
                                const activeBorderColor = tab.borderColor || theme.palette.primary.main;
                                const inactiveBorderColor = theme.palette.border?.grey || "#E2E2E9";
                                const activeBackgroundColor = (color === "primary"
                                    ? theme.palette.background.grey
                                    : theme.palette.primary.contrastText) ||
                                    theme.palette.background.default;
                                return {
                                    ...baseTabSx,
                                    minWidth: 0,
                                    padding: { xs: "24px", md: "28px 32px" },
                                    maxWidth: "none",
                                    borderLeft: "3px solid",
                                    borderColor: value === index
                                        ? activeBorderColor
                                        : inactiveBorderColor,
                                    transition: "background-color 150ms ease, border-color 150ms ease",
                                    ...(!(value === index) && {
                                        "&:hover": {
                                            cursor: "pointer",
                                            borderColor: `color-mix(in srgb, ${activeBorderColor} 40%, transparent)`,
                                            backgroundColor: `color-mix(in srgb, ${activeBackgroundColor} 60%, transparent)`,
                                        },
                                    }),
                                    "&.Mui-selected": {
                                        backgroundColor: activeBackgroundColor,
                                    },
                                };
                            } }, tab.id))) }) }), _jsx(Stack, { sx: { ...(direction === "vertical" && { width: { md: "60%" } }) }, children: dynamicList?.map((tab, index) => (_jsx(Fade, { in: value === index, timeout: 800, children: _jsx(CustomTabPanel, { value: value, index: index, dir: direction, tab: tab, children: tab.dynamicPart }, `${tab.title}-${index}`) }))) })] }));
};
export default DescriptionTabsComponent;
